import { create } from 'zustand'

import type { PriceSegmentOption, PriceSegmentOptions } from '@/types'

const EMPTY_SEGMENTS: PriceSegmentOption[] = []

interface PriceSegmentsStateInterface {
  clientKey: string | null
  options: PriceSegmentOptions | null
  selected: PriceSegmentOption[]
  segmentPricesLoading: boolean
  setOptions: (clientKey: string, options: PriceSegmentOptions) => void
  setSelected: (clientKey: string, selected: PriceSegmentOption[]) => void
  setSegmentPricesLoading: (segmentPricesLoading: boolean) => void
  reset: () => void
}

const initialState = {
  clientKey: null,
  options: null,
  selected: EMPTY_SEGMENTS,
  segmentPricesLoading: false,
}

const bySortOrder = (a: PriceSegmentOption, b: PriceSegmentOption) =>
  a.sortOrder - b.sortOrder

export const usePriceSegmentsStore = create<PriceSegmentsStateInterface>()(
  (set, get) => ({
    ...initialState,
    setOptions: (clientKey, options) =>
      set(
        get().clientKey === clientKey
          ? { options }
          : { clientKey, options, selected: EMPTY_SEGMENTS }
      ),
    setSelected: (clientKey, selected) =>
      set({
        clientKey,
        selected: selected
          .filter((segment) => !segment.isContext)
          .sort(bySortOrder),
      }),
    setSegmentPricesLoading: (segmentPricesLoading) =>
      set({ segmentPricesLoading }),
    reset: () => set(initialState),
  })
)

export const getPriceSegmentsClientKey = (
  clientId: string | null | undefined
) => clientId ?? ''

export const getSelectedPriceSegmentCodes = (clientKey: string) => {
  const { clientKey: selectionKey, selected } = usePriceSegmentsStore.getState()
  return selectionKey === clientKey ? selected.map(({ code }) => code) : []
}

export const useSelectedPriceSegments = (clientKey: string) =>
  usePriceSegmentsStore((state) =>
    state.clientKey === clientKey ? state.selected : EMPTY_SEGMENTS
  )

export const usePriceSegmentOptions = (clientKey: string) =>
  usePriceSegmentsStore((state) =>
    state.clientKey === clientKey ? state.options : null
  )
